import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Users, ShieldCheck, MapPin, Trophy } from "lucide-react";

import { listPlayers, listTeams, listStadiums } from "../../api/coredata.api";
import { listCompetitions } from "../../api/competitions.api";
import { listMatches } from "../../api/matches.api";
import { playerDisplayName } from "../../hooks/useNameMode";

// Top-nav cross-entity search. Hits players + teams + stadiums +
// competitions + matches in parallel (debounced) and shows grouped,
// keyboard-navigable results. Press `/` to focus; Esc to close;
// Up/Down to move; Enter to open.
//
// We hit the existing list endpoints (which already accept `search=`),
// not a new "search everything" backend route. Page=1 + limited columns
// keeps the response small. Failures per-bucket are swallowed silently
// so a single broken endpoint doesn't kill the whole search.
const GROUPS = [
  { key: "players",      label: "Players",      icon: Users,       fetch: (q) => listPlayers(1, { search: q }), pathOf: (r) => `/cricket/players/${r.id}/profile`, labelOf: (r) => playerDisplayName(r) || `${r.first_name || ""} ${r.last_name || ""}`.trim() },
  { key: "teams",        label: "Teams",        icon: ShieldCheck, fetch: (q) => listTeams(1, { search: q }),   pathOf: (r) => `/cricket/teams/${r.id}/edit`,      labelOf: (r) => r.name },
  { key: "stadiums",     label: "Stadiums",     icon: MapPin,      fetch: (q) => listStadiums(1, { search: q }),pathOf: (r) => `/cricket/stadiums/${r.id}/edit`,   labelOf: (r) => r.name + (r.city ? ` · ${r.city}` : "") },
  { key: "competitions", label: "Competitions", icon: Trophy,      fetch: (q) => listCompetitions(1, { search: q }), pathOf: (r) => `/cricket/competitions/${r.id}/edit`, labelOf: (r) => r.name },
  { key: "matches",      label: "Matches",      icon: Trophy,      fetch: (q) => listMatches(1, { search: q }), pathOf: (r) => `/cricket/matches/${r.id}/scorecard`, labelOf: (r) => `${r.team1?.name || "T1"} vs ${r.team2?.name || "T2"}` + (r.match_date ? ` · ${String(r.match_date).slice(0, 10)}` : "") },
];

const MAX_PER_GROUP = 5;

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({}); // {group: [{id, label, path}]}
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Global `/` focuses the search; ignored while typing in another input.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "input" || tag === "textarea" || tag === "select") return;
        if (e.target?.isContentEditable) return;
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Debounced parallel search across groups.
  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) {
      setResults({});
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      const out = {};
      await Promise.all(GROUPS.map(async (g) => {
        try {
          const res = await g.fetch(q.trim());
          const rows = (res.data?.results || []).slice(0, MAX_PER_GROUP);
          out[g.key] = rows.map((r) => ({
            id: r.id,
            label: g.labelOf(r),
            path: g.pathOf(r),
          }));
        } catch {
          out[g.key] = [];
        }
      }));
      setResults(out);
      setLoading(false);
      setActiveIdx(0);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Flatten for keyboard navigation.
  const flatHits = useMemo(() => {
    const list = [];
    for (const g of GROUPS) {
      for (const r of (results[g.key] || [])) {
        list.push({ ...r, group: g });
      }
    }
    return list;
  }, [results]);

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, flatHits.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const hit = flatHits[activeIdx];
      if (hit) {
        window.location.assign(hit.path);
        setOpen(false);
      }
    }
  };

  let runningIdx = 0;
  const totalHits = flatHits.length;

  return (
    <div ref={wrapRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search players, teams, matches…"
          className="w-full bg-bg-secondary border border-border-default rounded-md text-xs py-1.5 pl-8 pr-12 text-text-primary placeholder-text-secondary focus:outline-none focus:border-button-primary"
        />
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:inline-block text-[10px] font-mono px-1.5 py-0.5 rounded bg-bg-main border border-border-default text-text-secondary">
          /
        </kbd>
        {q && (
          <button
            onClick={() => { setQ(""); setResults({}); inputRef.current?.focus(); }}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
            title="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-bg-primary border border-border-default rounded-lg shadow-2xl max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-text-secondary">Searching…</div>
          )}
          {!loading && totalHits === 0 && (
            <div className="px-3 py-3 text-xs text-text-secondary">No matches.</div>
          )}
          {!loading && totalHits > 0 && (
            <div className="py-1">
              {GROUPS.map((g) => {
                const hits = results[g.key] || [];
                if (!hits.length) return null;
                return (
                  <div key={g.key} className="px-1 py-1">
                    <div className="text-[10px] uppercase tracking-wider text-text-secondary px-2 py-1 flex items-center gap-1">
                      <g.icon className="w-3 h-3" /> {g.label}
                    </div>
                    {hits.map((h) => {
                      const idx = runningIdx++;
                      const active = idx === activeIdx;
                      return (
                        <Link
                          key={h.id}
                          to={h.path}
                          onClick={() => setOpen(false)}
                          className={`block text-sm px-2 py-1.5 rounded ${
                            active
                              ? "bg-button-primary/20 text-text-primary"
                              : "text-text-secondary hover:bg-bg-secondary"
                          }`}
                          onMouseEnter={() => setActiveIdx(idx)}
                        >
                          {h.label}
                        </Link>
                      );
                    })}
                  </div>
                );
              })}
              <div className="border-t border-border-default px-3 py-1.5 text-[10px] text-text-secondary flex justify-between">
                <span>↑↓ navigate · Enter open · Esc close</span>
                <span>{totalHits} result{totalHits === 1 ? "" : "s"}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
